import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { GET_PRESET_EXPENSES } from '../../graphql/presetExpensesAndHourlyEmployees';

const PresetExpenseSelector = ({ onAddPresetExpenses }) => {
  const [selectedPresets, setSelectedPresets] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  // Fetch preset expenses from database
  const { data, loading } = useQuery(GET_PRESET_EXPENSES);
  const presetExpenses = data?.presetExpenses || [];

  const handleTogglePreset = (presetId) => {
    setSelectedPresets(prev => 
      prev.includes(presetId)
        ? prev.filter(id => id !== presetId)
        : [...prev, presetId]
    );
  };

  const handleSelectAll = () => {
    if (selectedPresets.length === presetExpenses.length) {
      setSelectedPresets([]);
    } else {
      setSelectedPresets(presetExpenses.map(preset => preset.id));
    }
  };

  const handleAddSelected = () => {
    if (selectedPresets.length === 0) {
      alert('Please select at least one preset expense');
      return;
    }

    // Convert selected presets to expense format
    const selectedPresetExpenses = presetExpenses.filter(preset => selectedPresets.includes(preset.id));
    const expensesToAdd = selectedPresetExpenses.map(preset => ({
      id: `preset_${preset.id}_${Date.now()}`,
      description: preset.name,
      amount: preset.amount,
      date: new Date().toISOString().split('T')[0],
      isNew: true,
      isFromPreset: true,
      presetId: preset.id
    }));
    
    onAddPresetExpenses(expensesToAdd);
    
    // Reset selection and close
    setSelectedPresets([]);
    setIsOpen(false);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(Number(amount));
  };

  const totalSelectedAmount = selectedPresets.reduce((total, presetId) => {
    const preset = presetExpenses.find(p => p.id === presetId);
    return total + (preset ? preset.amount : 0);
  }, 0);

  if (presetExpenses.length === 0) {
    return null; // Don't show if no presets exist
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 text-sm font-medium text-sky-600 bg-sky-50 border border-sky-200 rounded-md hover:bg-sky-100 focus:outline-none focus:ring-2 focus:ring-sky-500"
      >
        <div className="flex items-center justify-center space-x-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          <span>Add Preset Expenses ({presetExpenses.length})</span>
        </div>
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-2 w-full bg-white shadow-lg max-h-80 rounded-md border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-4 text-center text-gray-500">
              Loading preset expenses...
            </div>
          ) : (
            <>
              {/* Header with select all */}
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handleSelectAll}
                      className="text-sm font-medium text-sky-600 hover:text-sky-800"
                    >
                      {selectedPresets.length === presetExpenses.length ? 'Deselect All' : 'Select All'}
                    </button>
                    {selectedPresets.length > 0 && (
                      <span className="text-sm text-gray-500">
                        ({selectedPresets.length} selected)
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Preset expenses list */}
              <div className="max-h-48 overflow-y-auto">
                {presetExpenses.map((preset) => (
                  <div
                    key={preset.id}
                    className={`px-4 py-3 border-b border-gray-100 last:border-b-0 cursor-pointer hover:bg-gray-50 ${
                      selectedPresets.includes(preset.id) ? 'bg-sky-50' : ''
                    }`}
                    onClick={() => handleTogglePreset(preset.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={selectedPresets.includes(preset.id)}
                          onChange={() => handleTogglePreset(preset.id)}
                          className="h-4 w-4 text-sky-600 focus:ring-sky-500 border-gray-300 rounded"
                        />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{preset.name}</p>
                        </div>
                      </div>
                      <span className="text-sm font-medium text-gray-900">
                        {formatCurrency(preset.amount)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer with total and add button */}
              {selectedPresets.length > 0 && (
                <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="text-sm">
                      <span className="font-medium text-gray-900">Total: </span>
                      <span className="font-bold text-sky-600">
                        {formatCurrency(totalSelectedAmount)}
                      </span>
                    </div>
                    <button
                      onClick={handleAddSelected}
                      className="px-4 py-2 text-sm font-medium text-white bg-sky-600 border border-transparent rounded-md hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500"
                    >
                      Add Selected ({selectedPresets.length})
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PresetExpenseSelector;